import { useCallback } from 'react'

import { postCrossAppDndWatchMoveJobToChildIframes } from '@inno-tools/container-bridge'
import { DEFAULT_SHORTCUT_ITEM_SIZE } from 'modules/desktop/constants'
import { filesApi } from 'modules/desktop/modules/files-integration/api/filesApi'
import { initFilesSdk } from 'modules/desktop/modules/files-integration/api/filesSdk'
import { getAppFolderId } from 'modules/desktop/modules/files-integration/api/getAppFolderId'

import { useDesktopStore } from '../../../stores'
import { useShortcutGroupStore } from '../../../stores/shortcutGroupStore'
import { computeShortcutGroupTargets, pendingItemPositions } from '../utils'
import { getShortcutGroupCollisionItems } from '../utils/shortcutGroupBounds'

type GridPosition = { row: number; col: number }

/**
 * Moves items dragged in from a files-app window into the desktop's app-root
 * folder, recording their drop slots so the files-list sync can place them.
 */
export const useMoveFilesItemsToDesktop = (cols: number, rows: number) =>
  useCallback(
    async (itemIds: number[], dropPosition: GridPosition | null) => {
      if (!itemIds.length) return

      if (dropPosition) {
        // Resolve the final, non-overlapping slots up front against the live
        // desktop (kept-together block, edge-clamped, gap-packed when crowded)
        // — the same layout the drag preview shows — and seed them so the
        // files-list sync drops each item exactly where it was previewed.
        const desktopItems = useDesktopStore.getState().items
        const shortcutGroups = useShortcutGroupStore.getState().groups
        const occupied = [
          ...desktopItems,
          ...getShortcutGroupCollisionItems(shortcutGroups, desktopItems)
        ]
        const positions = computeShortcutGroupTargets(
          dropPosition,
          itemIds.length,
          cols,
          rows,
          occupied,
          DEFAULT_SHORTCUT_ITEM_SIZE
        )
        itemIds.forEach((id, idx) => {
          const position = positions[idx]
          if (position) pendingItemPositions.set(id, position)
        })
      }

      const { filesSdk, success } = initFilesSdk()
      if (!success || !filesSdk) return

      const destinationFolderId = await getAppFolderId()
      if (destinationFolderId == null) return

      try {
        const { data } = await filesApi.post<{
          data: { job_id?: string; total_items?: number }
        }>('/items/move', {
          destination_item_id: destinationFolderId,
          source_item_ids: itemIds
        })
        const jobId = data.data.job_id
        const totalItems =
          typeof data.data.total_items === 'number'
            ? data.data.total_items
            : itemIds.length
        if (jobId) {
          postCrossAppDndWatchMoveJobToChildIframes({ jobId, totalItems })
        }
      } catch (err) {
        itemIds.forEach(id => pendingItemPositions.delete(id))
        throw err
      }
    },
    [cols, rows]
  )
